import React, { useState, useEffect } from "react";
import {
  Drawer,
  IconButton,
  List,
  Select,
  MenuItem,
  Divider,
} from "@material-ui/core";
import {
  Home as HomeIcon,
  NotificationsNone as NotificationsIcon,
  FormatSize as TypographyIcon,
  FilterNone as UIElementsIcon,
  BorderAll as TableIcon,
  QuestionAnswer as SupportIcon,
  LibraryBooks as LibraryIcon,
  HelpOutline as FAQIcon,
  ArrowBack as ArrowBackIcon,
  EmojiEvents as LeaderboardIcon,
  DateRange as CalendarIcon,
  Settings as SettingsIcon,
} from "@material-ui/icons";
import { useTheme } from "@material-ui/styles";
import { withRouter } from "react-router-dom";
import classNames from "classnames";

// styles
import useStyles from "./styles";

// components
import SidebarLink from "./components/SidebarLink/SidebarLink";
import Dot from "./components/Dot";
import JoinGroupDialog from "../JoinGroupDialog";
import LeaveGroupDialog from "../LeaveGroupDialog";

// context
import {
  useLayoutState,
  useLayoutDispatch,
  toggleSidebar,
} from "../../context/LayoutContext";
import {
  useGroupState,
  useGroupDispatch,
  selectGroup,
} from "../../context/GroupContext";

// flags
import enFlag from "../../localization/en/flag.png";
import esFlag from "../../localization/es/flag.png";

// localization
import {
  useTranslation,
  useLeagueNames,
  useLanguage,
} from "../../localization";

function Sidebar({ location }) {
  var classes = useStyles();
  var theme = useTheme();
  var t = useTranslation();
  var leagueOverrides = useLeagueNames();

  // global
  var { isSidebarOpened } = useLayoutState();
  var layoutDispatch = useLayoutDispatch();
  var { status, groups, selectedGroup, leagues, leagueNames, isAdmin } =
    useGroupState();
  var groupDispatch = useGroupDispatch();
  var { language, setLanguage } = useLanguage();

  // The list of leagues is driven by the user's currently selected group.
  // Display names come from /league_name (cached in GroupContext); fall back
  // to the raw slug while a name is still loading. Leagues sit under a
  // "LEAGUES" section header, followed by a single Leaderboards entry.
  var structure = [
    { id: "leagues-title", type: "title", label: t.sidebar.leagues },
    ...leagues.map(slug => ({
      id: "league-" + slug,
      label: leagueOverrides[slug] || leagueNames[slug] || slug,
      link: "/app/fixtures/" + slug,
      icon: <TableIcon />,
    })),
    { id: "leaderboards-divider", type: "divider" },
    {
      id: "calendar",
      label: t.sidebar.calendar,
      link: "/app/calendar",
      icon: <CalendarIcon />,
    },
    {
      id: "leaderboards",
      label: t.sidebar.leaderboards,
      link: "/app/leaderboards",
      icon: <LeaderboardIcon />,
    },
  ];

  // Admin tools sit pinned to the bottom of the sidebar, shown only to admins of
  // the selected group. Rendered in a separate list so it can be pushed down.
  var bottomStructure = isAdmin
    ? [
        { id: "admin-divider", type: "divider" },
        {
          id: "admin",
          label: t.sidebar.admin,
          link: "/app/admin",
          icon: <SettingsIcon />,
        },
      ]
    : [];

  // local
  var [isPermanent, setPermanent] = useState(true);
  var [joinOpen, setJoinOpen] = useState(false);
  var [leaveOpen, setLeaveOpen] = useState(false);

  // Sentinels for the action items in the mobile group picker: selecting one
  // opens the matching dialog instead of switching the active group. Mirrors the
  // header's desktop group select.
  var JOIN_VALUE = "__join__";
  var LEAVE_VALUE = "__leave__";

  useEffect(function() {
    window.addEventListener("resize", handleWindowWidthChange);
    handleWindowWidthChange();
    return function cleanup() {
      window.removeEventListener("resize", handleWindowWidthChange);
    };
  });

  return (
    <Drawer
      variant={isPermanent ? "permanent" : "temporary"}
      className={classNames(classes.drawer, {
        [classes.drawerOpen]: isSidebarOpened,
        [classes.drawerClose]: !isSidebarOpened,
      })}
      classes={{
        paper: classNames(classes.drawerPaper, {
          [classes.drawerOpen]: isSidebarOpened,
          [classes.drawerClose]: !isSidebarOpened,
        }),
      }}
      open={isSidebarOpened}
    >
      <div className={classes.toolbar} />
      <div className={classes.mobileBackButton}>
        <IconButton onClick={() => toggleSidebar(layoutDispatch)}>
          <ArrowBackIcon
            classes={{
              root: classNames(classes.headerIcon, classes.headerIconCollapse),
            }}
          />
        </IconButton>
      </div>
      {/* The active-group picker and language switcher live in the header on
          desktop, but there's no room for them on a phone toolbar; in the mobile
          (temporary) drawer we surface them here instead. */}
      {!isPermanent ? (
        <div className={classes.mobileExtras}>
          {status === "ready" ? (
            <div className={classes.mobileGroupRow}>
              <span className={classes.mobileGroupLabel}>
                {t.header.activeGroup}
              </span>
              <Select
                value={selectedGroup || ""}
                onChange={e => {
                  if (e.target.value === JOIN_VALUE) {
                    setJoinOpen(true);
                    return;
                  }
                  if (e.target.value === LEAVE_VALUE) {
                    setLeaveOpen(true);
                    return;
                  }
                  selectGroup(groupDispatch, e.target.value);
                }}
                className={classes.mobileGroupSelect}
                disableUnderline
              >
                {groups.map(group => (
                  <MenuItem key={group.name} value={group.name}>
                    {group.name}
                  </MenuItem>
                ))}
                <Divider />
                <MenuItem value={JOIN_VALUE} className={classes.joinGroupItem}>
                  {t.header.joinGroup}
                </MenuItem>
                {selectedGroup && (
                  <MenuItem value={LEAVE_VALUE} className={classes.joinGroupItem}>
                    {t.header.leaveGroup}
                  </MenuItem>
                )}
              </Select>
            </div>
          ) : null}
          <div className={classes.mobileLangSwitcher}>
            <img
              src={enFlag}
              alt="English"
              onClick={() => setLanguage("en")}
              className={classNames(classes.mobileLangFlag, {
                [classes.mobileLangFlagActive]: language === "en",
              })}
            />
            <img
              src={esFlag}
              alt="Español"
              onClick={() => setLanguage("es")}
              className={classNames(classes.mobileLangFlag, {
                [classes.mobileLangFlagActive]: language === "es",
              })}
            />
          </div>
        </div>
      ) : null}
      <List className={classes.sidebarList}>
        {structure.map(link => (
          <SidebarLink
            key={link.id}
            location={location}
            isSidebarOpened={isSidebarOpened}
            {...link}
          />
        ))}
      </List>
      {bottomStructure.length > 0 ? (
        <List className={classes.bottomList}>
          {bottomStructure.map(link => (
            <SidebarLink
              key={link.id}
              location={location}
              isSidebarOpened={isSidebarOpened}
              {...link}
            />
          ))}
        </List>
      ) : null}
      <JoinGroupDialog open={joinOpen} onClose={() => setJoinOpen(false)} />
      <LeaveGroupDialog
        open={leaveOpen}
        group={selectedGroup}
        onClose={() => setLeaveOpen(false)}
      />
    </Drawer>
  );

  // ##################################################################
  function handleWindowWidthChange() {
    var windowWidth = window.innerWidth;
    var breakpointWidth = theme.breakpoints.values.md;
    var isSmallScreen = windowWidth < breakpointWidth;

    if (isSmallScreen && isPermanent) {
      setPermanent(false);
    } else if (!isSmallScreen && !isPermanent) {
      setPermanent(true);
    }
  }
}

export default withRouter(Sidebar);
